/**
 * ui-renderers/kili-sentient.ts
 * Kili Sentient features: Risk Fingerprint, Precedent Rail, Morning Brief, Voice Intent Badge.
 */

import { escapeHtml, escapeAttr, formatRelativeTime } from './core';

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────

export interface RiskSignal {
  type:       'limitation' | 'filing_window' | 'payment_arrears' | 'inactivity';
  severity:   'critical' | 'high' | 'medium';
  label:      string;
  statute:    string | null;
  days_left:  number | null;
  matter_id:  string;
  matter_ref: string;
}

export interface PrecedentHit {
  doctrine:  string;
  citation:  string;
  title:     string;
  url:       string;
  snippet?:  string;
}

export interface MorningBriefPayload {
  generatedAt:    number;
  hearings:       Array<{ title: string; matter_ref: string | null; start_time: number; location?: string }>;
  overdueTasks:   Array<{ title: string; due_date: number; matter_ref: string | null }>;
  unreadMessages: number;
  criticalRisks:  number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal constants
// ─────────────────────────────────────────────────────────────────────────────

const RISK_SEVERITY_COLOR: Record<string, string> = {
  critical: '#FF5F7A',
  high:     '#C9A96E',
  medium:   '#00E5FF',
};

const INTENT_LABELS: Record<string, string> = {
  attendance_note: '📋 Attendance Note',
  dictate_letter:  '✉ Letter',
  add_court_date:  '⚖ Court Date',
  add_task:        '✅ Task',
  dictate_memo:    '📄 Internal Memo',
  general_query:   '💬 General Query',
};

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export function renderRiskFingerprint(signals: RiskSignal[]): void {
  if (!signals || signals.length === 0) return;

  let container = document.getElementById('kili-risk-fingerprint');
  if (!container) {
    container = document.createElement('div');
    container.id = 'kili-risk-fingerprint';
    container.className = 'kili-risk-fp';
    const ctxBar = document.getElementById('ai-ctx-bar');
    if (ctxBar) {
      ctxBar.after(container);
    } else {
      document.querySelector('.ai-msgs')?.before(container);
    }
  }

  container.innerHTML = `
    <div class="kili-risk-fp-header">
      <span class="kili-risk-fp-icon">⚠</span>
      <span class="kili-risk-fp-title">Risk Radar</span>
      <button class="kili-risk-fp-close" aria-label="Dismiss risk panel">✕</button>
    </div>
    ${signals.slice(0, 4).map(s => `
      <div class="kili-risk-signal kili-risk-${s.severity}" title="${escapeAttr(s.statute || '')}">
        <span class="kili-risk-dot" style="background:${RISK_SEVERITY_COLOR[s.severity] || '#00e5ff'}"></span>
        <span class="kili-risk-content">
          <span class="kili-risk-ref">${escapeHtml(s.matter_ref)}</span>
          <span class="kili-risk-label">${escapeHtml(s.label)}</span>
          ${s.statute   ? `<span class="kili-risk-statute">${escapeHtml(s.statute)}</span>` : ''}
          ${s.days_left !== null ? `<span class="kili-risk-days kili-risk-days-${s.severity}">${s.days_left}d</span>` : ''}
        </span>
      </div>
    `).join('')}
  `;

  container.querySelector('.kili-risk-fp-close')?.addEventListener('click', () => {
    (container as HTMLElement).style.display = 'none';
  });
}

export function renderPrecedentRail(precedents: PrecedentHit[], bubble: Element): void {
  if (!precedents || !precedents.length || !bubble) return;

  const existing = bubble.querySelector('.kili-precedent-rail');
  if (existing) existing.remove();

  const rail = document.createElement('div');
  rail.className = 'kili-precedent-rail';
  rail.innerHTML = `
    <div class="kili-prec-label">⚖ Related Precedents</div>
    ${precedents.map(p => `
      <a class="kili-prec-pill"
         href="${escapeAttr(p.url)}"
         target="_blank" rel="noopener noreferrer">
        <span class="kili-prec-doctrine">${escapeHtml(p.doctrine)}</span>
        <span class="kili-prec-cite">${escapeHtml(p.citation || p.title)}</span>
        ${p.snippet ? `<span class="kili-prec-snippet">${escapeHtml(p.snippet.slice(0, 90))}…</span>` : ''}
      </a>
    `).join('')}
  `;

  const bodyWrap = bubble.querySelector('.ai-msg-body');
  if (bodyWrap) bodyWrap.appendChild(rail);
}

export function renderMorningBrief(brief: MorningBriefPayload): void {
  if (!brief) return;

  const hasContent = (brief.hearings?.length > 0)
    || (brief.overdueTasks?.length > 0)
    || (brief.unreadMessages > 0)
    || (brief.criticalRisks > 0);

  if (!hasContent) return;

  const convBody = document.getElementById('ai-msgs');
  if (!convBody || convBody.children.length > 0) return;

  const now     = new Date();
  const timeStr = now.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString('en-KE', { weekday: 'long', day: 'numeric', month: 'long' });

  const card = document.createElement('div');
  card.className = 'kili-morning-brief';
  card.innerHTML = `
    <div class="kmb-header">
      <span class="kmb-icon">🌅</span>
      <div class="kmb-titles">
        <div class="kmb-title">Good morning — here's your day</div>
        <div class="kmb-date">${dateStr} · ${timeStr}</div>
      </div>
    </div>

    ${brief.criticalRisks > 0 ? `
    <div class="kmb-section kmb-critical">
      <div class="kmb-section-label">🔴 Critical Deadlines</div>
      <div class="kmb-item">${brief.criticalRisks} matter${brief.criticalRisks !== 1 ? 's' : ''} with limitation periods expiring within 30 days</div>
    </div>` : ''}

    ${brief.hearings && brief.hearings.length > 0 ? `
    <div class="kmb-section">
      <div class="kmb-section-label">📅 Today's Schedule</div>
      ${brief.hearings.map(h => `
        <div class="kmb-item">
          <span class="kmb-time">${new Date(h.start_time * 1000).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })}</span>
          <span class="kmb-item-text">${escapeHtml(h.title)}</span>
          ${h.matter_ref ? `<span class="kmb-matter-ref">${escapeHtml(h.matter_ref)}</span>` : ''}
          ${h.location   ? `<span class="kmb-location">${escapeHtml(h.location)}</span>` : ''}
        </div>
      `).join('')}
    </div>` : ''}

    ${brief.overdueTasks && brief.overdueTasks.length > 0 ? `
    <div class="kmb-section">
      <div class="kmb-section-label">⚠ Overdue Tasks</div>
      ${brief.overdueTasks.map(t => `
        <div class="kmb-item kmb-overdue">
          <span class="kmb-item-text">${escapeHtml(t.title)}</span>
          ${t.matter_ref ? `<span class="kmb-matter-ref">${escapeHtml(t.matter_ref)}</span>` : ''}
          <span class="kmb-overdue-date">Due ${formatRelativeTime(t.due_date)}</span>
        </div>
      `).join('')}
    </div>` : ''}

    ${brief.unreadMessages > 0 ? `
    <div class="kmb-section">
      <div class="kmb-item">
        💬 <strong>${brief.unreadMessages}</strong> unread client message${brief.unreadMessages !== 1 ? 's' : ''} overnight
      </div>
    </div>` : ''}

    <div class="kmb-footer">What would you like to start with?</div>
  `;

  convBody.prepend(card);
}

export function showVoiceIntentBadge(
  intent: string,
  extractedData?: { person?: string; date?: string; matter_ref?: string },
): void {
  const label = INTENT_LABELS[intent] || intent;
  const existing = document.querySelector('.kili-voice-intent-badge');
  if (existing) existing.remove();

  const badge = document.createElement('div');
  badge.className = 'kili-voice-intent-badge';

  const details = [
    extractedData?.person     ? `Person: ${extractedData.person}`     : null,
    extractedData?.date       ? `Date: ${extractedData.date}`         : null,
    extractedData?.matter_ref ? `Matter: ${extractedData.matter_ref}` : null,
  ].filter(Boolean);

  badge.innerHTML = `
    <span class="kivi-badge-label">${label}</span>
    ${details.length ? `<span class="kivi-badge-details">${details.join(' · ')}</span>` : ''}
  `;

  const inputWrap = document.querySelector<HTMLElement>('.ai-input-wrap') || document.getElementById('ai-input-row');
  if (inputWrap) inputWrap.before(badge);

  setTimeout(() => { if (badge.parentNode) badge.remove(); }, 3500);
}
